
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Entity } from './types';
import { Users, Loader2, Shield, AlertCircle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface EntitySelectionProps {
  entities: Entity[];
  selectedEntities: string[];
  toggleEntitySelection: (id: string) => void;
  loading?: boolean;
}

export const EntitySelection = ({ 
  entities, 
  selectedEntities, 
  toggleEntitySelection,
  loading = false
}: EntitySelectionProps) => {
  const isMobile = useIsMobile();
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'empty':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  // Function to get compliance color based on score
  const getComplianceColor = (score: number = 0) => {
    if (score >= 90) return "text-green-500";
    if (score >= 70) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <Card className="h-full">
      <CardHeader className="py-3 md:py-4">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Teams to Process</span>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="px-3 pb-3 md:px-4 md:pb-4 pt-0">
        {loading ? (
          // Loading skeletons
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center space-x-2 p-2">
                <Skeleton className="h-4 w-4 rounded-sm" />
                <Skeleton className="h-6 w-full" />
              </div>
            ))}
          </div>
        ) : entities.length > 0 ? (
          <div className={cn(
            "space-y-2", 
            isMobile && "flex flex-wrap gap-2 space-y-0"
          )}>
            {entities.map((entity) => (
              <div 
                key={entity.id}
                className={cn(
                  "flex items-center space-x-2 p-2 rounded-md transition-colors",
                  isMobile ? "flex-1 min-w-[45%]" : "w-full",
                  selectedEntities.includes(entity.id) 
                    ? "bg-muted/70 dark:bg-slate-800/50" 
                    : "hover:bg-muted/50 dark:hover:bg-slate-800/30"
                )}
                onClick={() => toggleEntitySelection(entity.id)}
              >
                <Checkbox 
                  checked={selectedEntities.includes(entity.id)}
                  onCheckedChange={() => toggleEntitySelection(entity.id)}
                  id={`entity-${entity.id}`}
                />
                <div className="flex items-center flex-1 space-x-2 text-sm">
                  <div className="flex-shrink-0">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <label 
                    htmlFor={`entity-${entity.id}`} 
                    className="flex-1 cursor-pointer truncate font-medium"
                  >
                    {entity.name}
                  </label>
                  {entity.compliance && (
                    <div className={cn(
                      "flex items-center space-x-1",
                      getComplianceColor(entity.compliance)
                    )}>
                      <Shield className="h-3.5 w-3.5" />
                      <span className="text-xs">{entity.compliance}%</span>
                    </div>
                  )}
                  <span className={cn(
                    "px-1.5 py-0.5 text-xs rounded-full",
                    getStatusColor(entity.status)
                  )}>
                    {entity.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p>No teams available</p>
            <p className="text-xs mt-1">Connect to Microsoft Graph or select a team to continue</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
